import React, { useState } from 'react';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheck } from '@fortawesome/pro-solid-svg-icons';

// actions + utils
import { getCookie } from '../../../common/utils/cookie';
import { isRequired, isValid } from '../../../utils/form';
import { formatOEC } from '../../../utils/enroll';
import {
  submitEnrollment,
  updateEnrollment,
  getEnrollment,
} from '../../../actions/enrollment';

// components
import { LoaderButton, SummaryBox } from '../../../common/components';
import HRAReminder from './HRAReminder';

function getSectionStatus({
  section,
  form,
  values,
  validation,
}) {
  let fieldCount = 0;
  let validCount = 0;

  section.pages.forEach(page => (
    page.fields.forEach(field => {
      const value = values[field.name];
      const validationRegex = validation[field.name];
      if (isRequired({ form, name: field.name, currentValues: values })) {
        fieldCount += 1;
        if ((value !== undefined) && isValid({ value, validationRegex })) {
          validCount += 1;
        }
      } else if (value !== undefined && value !== null && value !== '') {
        fieldCount += 1;
        if (isValid({ value, validationRegex })) validCount += 1;
      }
    })
  ));

  const isComplete = (fieldCount === validCount);
  const noFields = (fieldCount === 0);

  return {
    fieldCount,
    validCount,
    isComplete,
    noFields,
  };
}

function SectionStatus({ section, status }) {
  let icon = <h4><FontAwesomeIcon icon={faTimes} style={{ color: 'var(--bs-danger)' }} /></h4>;

  if (status.isComplete) {
    icon = <h4><FontAwesomeIcon icon={faCheck} style={{ color: 'var(--bs-success)' }} /></h4>;
  }
  /*
  if (status.noFields) {
    icon = <h4 style={{ color: 'var(--bs-gray-500)' }}>N/A</h4>
  }
  */

  return !status.noFields && (
    <div className="row justify-content-between">
      <div className="col-md-8">
        <h4 className={status.noFields ? 'text-muted' : ''}>{section.navTitle}</h4>
      </div>
      <div className="col">
        {icon}
      </div>
    </div>
  );
}

function ConfirmationBox() {
  const {
    confirmationNumber,
    submitDate,
    finalSubmissionToCarrier,
    finalCarrierSubmitDate,
    cancelled,
  } = useSelector(store => store.enrollment);

  let sd = moment(submitDate);
  if (sd.isValid()) {
    sd = moment.tz(sd, moment.tz.guess()).format('MM/DD/YYYY HH:mm z');
  } else {
    sd = <span className="text-danger">Invalid Submission Date</span>;
  }
  let submissionToCarrierText = 'Pending';
  if (finalSubmissionToCarrier) {
    submissionToCarrierText = moment.tz(finalCarrierSubmitDate, moment.tz.guess()).format('MM/DD/YYYY HH:mm z');
  } else if (cancelled) {
    submissionToCarrierText = 'Cancelled';
  }
  const data = [
    { title: 'Confirmation number:', desc: confirmationNumber },
    { title: 'Submit date:', desc: sd },
    { title: 'Submission to carrier: ', desc: submissionToCarrierText },
  ];

  return (
    <div className="row mt-3 gx-0">
      <div className={`col-md-12 alert alert-${cancelled ? 'danger' : 'success'} shadow-sm`}>
        {data.map(d => (
          <div key={`confirmation-box-${d.title}`} className="row justify-content-between">
            <div style={{ height: '45px' }} className="d-flex col-6 align-items-center">
              <h4 className="mb-0">{d.title}</h4>
            </div>
            <div className="d-flex col-5 justify-content-start align-items-center">
              <h4 className="mb-0">{d.desc}</h4>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReviewBox() {
  const {
    plan: {
      cpsID,
      planName,
      planType,
      plan: { TotalPlanPremium } = {},
    },
    shopper: {
      firstName,
      middleName,
      lastName,
    },
    values: { effective_date: effectiveDate = '[NO_EFFECTIVE_DATE]' },
  } = useSelector(store => store);

  return (
    <div className="row">
      <div className="col-md-12">
        <h4>Review</h4>
        <div className="mb-4">
          You’re almost finished with your application for coverage. Please review the information you provided
          and your responses to the questions in the application before submitting your application.
        </div>
        <div className="mb-4">
          <SummaryBox
            planName={planName}
            contractID={cpsID}
            planType={planType}
            planPremium={TotalPlanPremium}
            effectiveDate={effectiveDate}
            firstName={firstName}
            middleName={middleName}
            lastName={lastName}
          />
        </div>
      </div>
    </div>
  );
}

function Summary() {
  const dispatch = useDispatch();
  const {
    form: {
      form,
      submitType,
      carrierName,
    },
    enrollment: {
      errors: submitErrors = [],
      submittedFlag: submitted,
      confirmationNumber,
    },
    plan: { plan = {} },
    values,
    validation,
    quote,
    agent,
    theme,
  } = useSelector(store => store);
  const [ loading, setLoading ] = useState(false);
  const [ refreshing, setRefreshing ] = useState(false);

  const statuses = form.map(section => {
    const { isComplete } = getSectionStatus({
      section,
      form,
      values,
      validation,
    });
    return isComplete;
  });

  async function handleSubmit() {
    setLoading(true);
    // clear existing errors
    dispatch({ type: 'SET_ENROLLMENT_FIELD', payload: { errors: [] } });
    const { formattedData, agentID } = formatOEC(carrierName, {
      agent,
      quote,
      fields: values,
      plan,
      theme,
      confirmationNumber,
    });

    try {
      // make sure the final enrollment fields get saved to enrollment.fields
      const meetType = (getCookie('meetType') || 'inPerson');
      const extendedValues = {
        ...values,
        meetType,
        carrier_agent_id: agentID,
      };
      if (meetType.includes('telephonic')) extendedValues.esign_signature = 'telephonic';
      await updateEnrollment({ values: extendedValues });
      const enrollment = await submitEnrollment(quote.quoteID, submitType, formattedData);
      dispatch({
        type: 'SET_ENROLLMENT_FIELD',
        payload: {
          submittedFlag: true,
          finalSubmissionToCarrier: enrollment.finalSubmissionToCarrier,
          finalCarrierSubmitDate: enrollment.finalCarrierSubmitDate,
          submitDate: enrollment.submitDate,
        },
      });
      // set a submitted flag in the values object so that we can display
      // scripting based on this field
      dispatch({ type: 'SET_FIELD_VALUE', name: 'submitted', value: true });
      setLoading(false);
    } catch ({ errors }) {
      console.log(errors);
      dispatch({ type: 'SET_ENROLLMENT_FIELD', payload: { errors } });
      setLoading(false);
    }
  }

  async function handleRefresh() {
    setRefreshing(true);
    try {
      const refreshedEnrollment = await getEnrollment(quote.quoteID);
      dispatch({
        type: 'SET_ENROLLMENT_FIELD',
        payload: {
          ...refreshedEnrollment,
          fields: { ...(refreshedEnrollment.fields || {}) },
        },
      });
      dispatch({ type: 'SET_FIELD_VALUEs', values: refreshedEnrollment.fields });
    } catch (err) {
      console.error(err);
    }
    setRefreshing(false);
  }

  let submitButtonDisabled = false;
  let shopperEsign = '';
  if (statuses.includes(false)) submitButtonDisabled = true;
  if (submitted) submitButtonDisabled = true;
  if (values.signature_type === 'email') {
    submitButtonDisabled = true;
    shopperEsign = 'email';
  }
  if (values.signature_type === 'sms') {
    submitButtonDisabled = true;
    shopperEsign = 'sms';
  }

  let noEsignSent = true;
  if ((values.esignature_email_timestamps || []).length >= 1) noEsignSent = false;
  if ((values.esignature_sms_timestamps || []).length >= 1) noEsignSent = false;

  return (
    <>
      <div className="row">
        <div className="col-md-12">
          <h3>{submitted ? 'Submitted!' : 'Summary'}</h3>
          <hr />
        </div>
      </div>
      {/* if not submitted */}
      {!submitted && form.map(section => {
        const status = getSectionStatus({
          section,
          form,
          values,
          validation,
        });
        return <SectionStatus key={section.navTitle} section={section} status={status} />;
      })}
      {/* if submitted */}
      {submitted && <ConfirmationBox />}
      {submitted && <HRAReminder />}
      <div className="row">
        <div className="col-md-12">
          <hr className="mt-4 mb-4" />
        </div>
      </div>
      {(values.signature_type === 'in_person') && !submitted && <ReviewBox />}
      <div className="row mb-2">
        <div className="col-md-4">
          {(shopperEsign && !submitted) ? (
            <LoaderButton
              className="btn btn-outline-secondary"
              text="Refresh"
              loadingText="Refreshing"
              loading={refreshing}
              onClick={() => handleRefresh()}
              disabled={refreshing || noEsignSent}
            />
          ) : (
            <LoaderButton
              className="btn btn-success"
              text={submitted ? 'Submitted' : 'Submit'}
              loadingText="Submitting"
              loading={loading}
              onClick={() => handleSubmit()}
              disabled={submitButtonDisabled}
            />
          )}
          <br />
          {noEsignSent && shopperEsign && (
            <span className="text-danger fs-5 fw-bold">
              You haven&apos;t sent an esign request to the client yet!
            </span>
          )}
        </div>
        {(shopperEsign && !submitted) ? (
          <div className="col-md-8">
            <span className="text-danger text-muted">
              <i>
                You have opted to send the shopper this enrollment and have them sign and submit themselves.
                You will see the confirmation number and submit time here once the shopper has submitted.
                Use the refresh button to see if the shopper has reviewed and submitted.
              </i>
            </span>
          </div>
        ) : (
          <div className="col-md-8">
            {Boolean(submitErrors.length) && (
              <div className="alert alert-warning">
                <ul className="mb-0">
                  {submitErrors.map(err => (
                    <li>
                      <span className="me-2 fw-bold">{err}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
      {(values.signature_type === 'in_person') && (
        <div className="row mb-5">
          <div className="d-flex col justify-content-end">
            <span className="fs-5 text-muted">
              {theme.esignMaterialID}
            </span>
          </div>
        </div>
      )}
    </>
  );
}

export default Summary;
